import styled, {css} from 'styled-components';
import {Button, Table} from 'antd';
import {iconColor, typographyStyles} from '../../utils/styles';
import {getReportTokensCss} from '../../utils/helpers/report.helpers';

const StyledAgentChatSharedOptionButton = styled(Button)`
  margin: auto;
  width: max-content;

  &.ant-btn-variant-link {
    ${typographyStyles.body.regular};
    color: ${props => props.theme.colors.additional_purple};
  }
`;

const StyledAgentChatSharedConfirmationOption = styled.div`
  align-items: center;
  border: 1px solid;
  border-radius: 4px;
  display: flex;
  font-size: 14px;
  padding: 6px 12px;
  width: 100%;

  .ant-space-item {
    display: flex;
  }

  ${props => (props.variant === 'confirm' || props.variant === 'option') && css`
    border-color: ${props => props.theme.colors.additional_purple};

    svg {
      ${({ theme }) => iconColor(theme.colors.additional_purple)}
    }
  `}

  ${props => props.variant === 'cancel' && css`
    border-color: ${props => props.theme.colors.functional_danger_d};

    svg {
      ${({ theme }) => iconColor(theme.colors.functional_danger_d)}
    }
  `}

  &:hover {
    cursor: pointer;
  }
`;

const StyledAgentChatSharedTable = styled(Table)`
  margin: 8px 0;

  .ant-table {
    &.ant-table-small {
      .ant-table-thead {
        font-size: 12px;
      }
      .ant-table-tbody {
        font-size: 13px;
      }
    }
    border-radius: 8px;
  }

  .ant-table-container table > thead {
    & > tr:first-child >*:first-child {
      border-start-start-radius: 8px;
    }

    & > tr:first-child >*:last-child {
      border-start-end-radius: 8px;
    }
  }

  .ant-table-thead > tr > th {
    background-color: #FAFBFC;
    color: ${props => props.theme.colors.neutral_7};
    text-transform: uppercase;

    &:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before {
      background-color: #FAFBFC;
    }
  }

  .ant-table-footer {
    background-color: #FAFBFC;
    color: ${props => props.theme.colors.neutral_7};
    border-start-start-radius: 8px;
    border-start-end-radius: 8px;
  }
`;

const StyledAgentChatSharedQuickActionChipButton = styled(Button)`
  border-color: ${props => props.theme.colors.neutral_4};
  border-radius: 18px;
  box-shadow: none !important;
  color: ${props => props.theme.colors.neutral_10};
  height: 34px;

  &:not(:disabled):not(.ant-btn-disabled):hover {
    border-color: ${props => props.theme.colors.primary_5} !important;
    color: ${props => props.theme.colors.primary_5} !important;
  }

  ${props => props.$isActive && `
    border-color: ${props.theme.colors.primary_5} !important;
    background-color: ${props.theme.colors.primary_1} !important;
    color: ${props.theme.colors.primary_5} !important;

    &:not(:disabled):not(.ant-btn-disabled):hover {
      color: ${props.theme.colors.primary_5} !important;
    }
  `}
`;

// Overrides @ant-design/x's default 236px-wide attachment card — sized for a full-page
// uploader, not a chat composer footer where several staged files need to fit side by side.
const StyledAgentChatSharedAttachmentsPreview = styled.div`
  .ant-attachment-list {
    gap: 6px;
    padding: 8px 4px;
  }

  .ant-attachment-list-card.ant-attachment-list-card-type-overview {
    align-items: center;
    height: 40px;
    padding: 0 8px;
    width: 160px;

    .ant-attachment-list-card-icon {
      font-size: 16px;
      padding-top: 0;
    }

    .ant-attachment-list-card-name,
    .ant-attachment-list-card-desc {
      font-size: 12px;
    }
  }

  .ant-attachment-list-card.ant-attachment-list-card-type-preview,
  .ant-attachment-list-upload-btn {
    height: 40px;
    width: 40px;
  }

  .ant-attachment-list-upload-btn {
    font-size: 16px;
  }
`;

const StyledAgentChatSharedDownloadButton = styled(Button)`
  align-items: center;
  border: 1px solid ${props => props.theme.colors.neutral_4};
  color: ${props => props.theme.colors.neutral_8};
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;

  &:not(:disabled):not(.ant-btn-disabled) {
    &:hover,
    &:focus {
      border-color: ${props => props.theme.colors.neutral_4};
      color: ${props => props.theme.colors.neutral_8};
      box-shadow: none;
    }
  }
`;

/**
 * The report's container: the source of its design tokens, and the box it may not paint outside of.
 *
 * ⚠ THE TOKENS LIVE HERE BECAUSE THEY CANNOT RIDE IN WITH THE HTML. The report template ships a
 * `:root` block of custom properties, which is right for the standalone
 * `cli.py reports render --fixture --open` path — but DOMPurify empties a `<style>` element
 * unconditionally, so in the app that block is gone before render. The template owns the token
 * NAMES and stays app-agnostic; this owns the VALUES (lib-docs#23).
 *
 * ⚠ THE TOKENS COME FROM `REPORT_TOKENS` (report.helpers.js), THE LIST PRINT SHARES. A printed report
 * is a separate document that copies these values off this element, so a token defined only here
 * would render in the chat and print as nothing.
 *
 * ⚠ `contain: paint` IS A SECURITY LINE, NOT A LAYOUT ONE. The report is untrusted HTML carrying
 * inline CSS; the sanitiser refuses `position` and `transform`, but a negative margin is ordinary
 * CSS. Paint containment clips whatever the report draws to this box, so no crafted report can lay
 * itself over the conversation around it.
 *
 * Deliberately no rules for the report's own elements: the templates carry their design inline, and a
 * second styling source here would be the one nobody checks. What cannot be inline is the exception —
 * the interaction states: hover, keyboard focus, a disabled download — and it lives below with
 * `!important`, because an inline style otherwise wins. Print rules do not: printing happens in its
 * own document (`printReport`).
 */
const StyledAgentChatSharedReport = styled.div`
  ${props => getReportTokensCss(props.theme)}

  color: var(--ink);
  /* ⚠ 100vw CLAMPED BY max-width, NOT width: 100%. A chat bubble sizes itself to its content, so a
     percentage width has nothing definite to resolve against: measured in the full-width chat, the
     report rendered 459px wide inside the 720px column, its four cards reflowed to two. Claiming the
     viewport's width makes the bubble grow to the room it has, and max-width stops it there — in the
     column and in the drawer alike. */
  width: 100vw;
  max-width: 100%;
  contain: paint;
  /* A report wider than its column, in the drawer, scrolls instead of being clipped by the
     containment above, which would hide a figure without saying so. */
  overflow-x: auto;

  /* The report's actions are real buttons, and a button does not inherit the page font. The font is
     the app's to give: a template may not set font-family inline. */
  button {
    font-family: inherit;
  }

  /* Only an action that does something looks clickable, and every one that does nothing is :disabled —
     a report still loading disables its downloads, and the app disables a download whose export is
     missing or whose link has expired (AgentChatReport). */
  button[data-action]:not(:disabled) {
    cursor: pointer;
  }

  /* Interaction states cannot be inline, and the template's inline background beats any normal rule,
     hence !important. The tint marks only an action that does something; the focus ring shows on
     every button the keyboard can reach. */
  button[data-action]:not(:disabled):hover {
    background: var(--tint) !important;
  }

  button[data-action]:focus-visible,
  summary:focus-visible {
    outline: 2px solid var(--primary) !important;
    outline-offset: 2px;
  }

  /* A disclosure's summary is a control, and a template may not set cursor inline (it is off the CSS allowlist). */
  summary {
    cursor: pointer;
  }

  /* A pivot selector's current angle reads as chosen, and pressing it again does nothing, so it does not look
     pressable. Before the disabled rule, so a selector waiting for its new report still looks disabled. */
  button[data-action='pivot'][aria-pressed='true'] {
    background: var(--tint) !important;
    border-color: var(--primary) !important;
    color: var(--primary) !important;
    cursor: default;
  }

  /* A disabled action looks the way the template draws one while the report loads. The app also disables
     one the template drew enabled, a CSV whose link has expired, so its inline colours are overridden. */
  button[data-action]:disabled {
    border-color: var(--border-card) !important;
    color: var(--muted) !important;
  }
`;

/** Why one of a report's downloads is disabled, written under the report: outside its wrapper, so it never prints. */
const StyledAgentChatSharedReportNote = styled.div`
  color: ${props => props.theme.colors.neutral_8};
  margin-top: 8px;
  ${typographyStyles.footnote.description};
`;

export {
  StyledAgentChatSharedReport,
  StyledAgentChatSharedReportNote,
  StyledAgentChatSharedOptionButton,
  StyledAgentChatSharedConfirmationOption,
  StyledAgentChatSharedTable,
  StyledAgentChatSharedQuickActionChipButton,
  StyledAgentChatSharedAttachmentsPreview,
  StyledAgentChatSharedDownloadButton,
}
